import Api from "../../../../../../../../../core/services/api/api";

const PART_FORMS_FETCH_CONCURRENCY = 6;

const associationCacheKey = (partId, formId) => `${partId}:${formId}`;

/**
 * In-memory part↔form association cache for one import transform run.
 */
export class PartFormAssociationLookupStore {
    constructor() {
        /** @type {Map<string, { partFormId: number, isRequired: boolean }>} */
        this._byPartAndForm = new Map();
    }

    clear() {
        this._byPartAndForm.clear();
    }

    /**
     * @param {number|string} partId
     * @param {string} formId
     * @returns {{ partFormId: number, isRequired: boolean } | null}
     */
    getAssociation(partId, formId) {
        if (partId == null || formId == null || formId === "") {
            return null;
        }
        return this._byPartAndForm.get(associationCacheKey(partId, formId)) ?? null;
    }

    /**
     * @param {number|string} partId
     * @param {string} formId
     * @param {number} partFormId
     * @param {boolean} [isRequired]
     */
    registerAssociation(partId, formId, partFormId, isRequired = false) {
        if (partId == null || formId == null || partFormId == null) {
            return;
        }
        this._byPartAndForm.set(associationCacheKey(partId, formId), {
            partFormId,
            isRequired: !!isRequired,
        });
    }

    /**
     * Preloads existing part-form links for the given part ids.
     *
     * @param {Array<number|string>} partIds
     */
    async preloadForPartIds(partIds) {
        const uniquePartIds = [
            ...new Set(
                (partIds ?? [])
                    .map((id) => (typeof id === "string" ? parseInt(id, 10) : id))
                    .filter((id) => id != null && !Number.isNaN(id)),
            ),
        ];

        if (!uniquePartIds.length) {
            return;
        }

        let nextIndex = 0;
        const worker = async () => {
            while (nextIndex < uniquePartIds.length) {
                const index = nextIndex;
                nextIndex += 1;
                const partId = uniquePartIds[index];
                const response = await Api.listPartForms(partId);
                if (!response?.resultFlag) {
                    continue;
                }
                for (const entry of response.data ?? []) {
                    const formId = entry?.form?.id;
                    if (formId == null || entry?.partFormId == null) {
                        continue;
                    }
                    this.registerAssociation(partId, formId, entry.partFormId, entry.isRequired);
                }
            }
        };

        const concurrency = Math.min(PART_FORMS_FETCH_CONCURRENCY, uniquePartIds.length);
        await Promise.all(Array.from({length: concurrency}, () => worker()));
    }
}
